import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

const FACE_STEPS = [
  { label: "Encrypting upload", duration: 1500 },
  { label: "Detecting facial landmarks", duration: 4000 },
  { label: "Calibrating PSL anchors", duration: 6000 },
  { label: "Computing 6-metric breakdown", duration: 6000 },
  { label: "Compiling improvement stack", duration: 4000 },
];

const FULL_STEPS = [
  { label: "Encrypting upload", duration: 1500 },
  { label: "Detecting face & landmarks", duration: 5000 },
  { label: "Analyzing frame & V-taper", duration: 6000 },
  { label: "Estimating body composition", duration: 6000 },
  { label: "Reading posture & halo signals", duration: 6000 },
  { label: "Calibrating PSL × masculinity index", duration: 8000 },
  { label: "Compiling 12-metric breakdown", duration: 6000 },
  { label: "Ranking improvement stack", duration: 5000 },
];

export default function ScanProgress({ mode }) {
  const steps = mode === "full" ? FULL_STEPS : FACE_STEPS;
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const advance = (i) => {
      if (cancelled || i >= steps.length - 1) return;
      const t = setTimeout(() => {
        if (!cancelled) {
          setActive(i + 1);
          advance(i + 1);
        }
      }, steps[i].duration);
      return t;
    };
    advance(0);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className="panel-bright p-6 mt-6" data-testid="scan-progress">
      <div className="flex items-center gap-3 mb-6">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--primary)]" />
        <span className="tape-label">{mode === "full" ? "FULL ANALYSIS RUNNING" : "FACE ANALYSIS RUNNING"}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] ml-auto">
          STEP {active + 1}/{steps.length}
        </span>
      </div>

      <div className="space-y-1">
        {steps.map((s, i) => {
          const done = i < active;
          const current = i === active;
          return (
            <div
              key={i}
              className={`grid grid-cols-12 gap-3 items-center px-3 py-2 transition-all ${
                current ? "bg-[var(--surface)]" : ""
              }`}
              data-testid={`progress-step-${i}`}
            >
              <div className="col-span-1 font-mono text-xs text-[var(--text-muted)]">0{i + 1}</div>
              <div className="col-span-7 text-sm font-mono">
                <span className={done ? "text-[var(--success)] line-through opacity-60" : current ? "text-white" : "text-[var(--text-muted)]"}>
                  {s.label}
                </span>
              </div>
              <div className="col-span-3 text-right">
                {current && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">PROCESSING</span>
                )}
                {done && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--success)]">DONE</span>
                )}
              </div>
              <div className="col-span-1 flex justify-end">
                <div
                  className={`w-2 h-2 ${
                    done ? "bg-[var(--success)]" : current ? "bg-[var(--primary)] animate-pulse" : "bg-[var(--border-bright)]"
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
        {mode === "full" ? "Full scans take 30–50 seconds. Hang tight." : "Face scans take 10–20 seconds."}
      </div>
    </div>
  );
}
